<template>
  <div class="fill-height">
    <template v-if="error || deleteError">
      <GenericError />
    </template>

    <template v-else>
      <div v-if="isLoading || deleteLoading" class="content-spinner">
        <v-progress-circular indeterminate size="75" color="primary"></v-progress-circular>
      </div>

      <div v-else-if="items.length > 0">
        <v-btn fixed dark fab bottom right color="warning" @click="$router.push({ name: 'UserCreate' })">
          <v-icon>mdi-plus</v-icon>
        </v-btn>

        <template>
          <v-data-table :headers="headers" :items="items" hide-default-footer class="table">
            <template slot="items" slot-scope="props">
              <td>{{ props.item.id }}</td>
              <td>{{ props.item.username }}</td>
              <td>{{ props.item.email }}</td>
              <td>{{ props.item.emailVerified }}</td>
              <td>{{ props.item.enabled }}</td>
              <td>{{ props.item.roleInVessels }}</td>
              <td>{{ props.item.roleInOrganizations }}</td>
              <td class="justify-end layout px-1">
                <v-btn
                  icon
                  class="mx-0"
                  @click="
                    $router.push({
                      name: 'UserUpdate',
                      params: { id: $iriToId(props.item['@id']) }
                    })
                  "
                >
                  <v-icon color="grey darken-1">mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon class="mx-0" @click="selectForDelete(props.item)">
                  <v-icon color="grey darken-1">mdi-delete</v-icon>
                </v-btn>
              </td>
            </template>
            <template v-if="view" slot="footer">
              <td colspan="100%" class="text-xs-right px-1">
                <v-btn
                  text
                  icon
                  color="grey darken-1"
                  :disabled="!view['hydra:previous']"
                  @click="getPage(view['hydra:first'])"
                >
                  <v-icon>mdi-page-first</v-icon>
                </v-btn>
                <v-btn
                  text
                  icon
                  color="grey darken-1"
                  :disabled="!view['hydra:previous']"
                  @click="getPage(view['hydra:previous'])"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn
                  text
                  icon
                  color="grey darken-1"
                  :disabled="!view['hydra:next']"
                  @click="getPage(view['hydra:next'])"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
                <v-btn
                  text
                  icon
                  color="grey darken-1"
                  :disabled="!view['hydra:next']"
                  @click="getPage(view['hydra:last'])"
                >
                  <v-icon>mdi-page-last</v-icon>
                </v-btn>
              </td>
            </template>
          </v-data-table>
        </template>

        <v-dialog v-model="deleteConfirmShow" max-width="400">
          <v-card v-if="selectedForDelete">
            <v-card-title class="headline">Delete {{ selectedForDelete.id }}?</v-card-title>
            <v-card-text>
              Are you sure you want to delete {{ selectedForDelete.id }}? <br /><br />
              The deleted data <strong>can't be recovered</strong>
            </v-card-text>
            <v-card-actions>
              <div class="flex-grow-1"></div>
              <v-btn color="grey darken-1" text @click.native="onDeleteCancel">Cancel</v-btn>
              <v-btn color="red" text @click.native="onDeleteConfirm">Delete</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>

      <div v-else class="d-flex fill-height text-center align-center justify-center" style="flex: 1">
        <div>
          <v-icon color="grey lighten-2" size="150px">mdi-plus-circle-outline</v-icon>
          <h1 class="headline">Create your first entry</h1>
          <p class="subheading">
            There are no elements yet.
          </p>
          <v-btn color="primary" @click="$router.push({ name: 'UserCreate' })">
            Create first entry
          </v-btn>
        </div>
      </div>

      <v-snackbar v-if="localCreatedItem" v-model="snackbar">
        {{ localCreatedItem.id }} successfully added.
        <v-btn text color="warning" @click.native="snackbar = false">Close</v-btn>
      </v-snackbar>
      <v-snackbar v-if="localUpdatedItem" v-model="snackbar">
        {{ localUpdatedItem.id }} successfully updated.
        <v-btn text color="warning" @click.native="snackbar = false">Close</v-btn>
      </v-snackbar>
      <v-snackbar v-if="localDeletedItem" v-model="snackbar">
        {{ localDeletedItem.id }} has been deleted.
        <v-btn text color="warning" @click.native="snackbar = false">Close</v-btn>
      </v-snackbar>
    </template>
  </div>
</template>

<script>
import GenericError from '@/components/error/GenericError'
import { mapActions, mapGetters } from 'vuex'

export default {
  components: {
    GenericError
  },

  data: () => ({
    snackbar: false,
    localDeletedItem: null,
    localUpdatedItem: null,
    localCreatedItem: null,
    deleteConfirmShow: false,
    selectedForDelete: null,
    headers: [
      { text: 'id', value: 'id' },
      { text: 'username', value: 'username' },
      { text: 'email', value: 'email' },
      { text: 'emailVerified', value: 'emailVerified' },
      { text: 'enabled', value: 'enabled' },
      { text: 'roleInVessels', value: 'roleInVessels' },
      { text: 'roleInOrganizations', value: 'roleInOrganizations' },
      { text: 'Actions', align: 'right', sortable: false }
    ]
  }),

  computed: mapGetters({
    deletedItem: 'user/del/deleted',
    error: 'user/list/error',
    items: 'user/list/items',
    isLoading: 'user/list/isLoading',
    view: 'user/list/view',
    createdItem: 'user/create/created',
    updatedItem: 'user/update/updated',
    deleteError: 'user/del/error',
    deleteLoading: 'user/del/isLoading'
  }),

  watch: {
    deletedItem: function(deleted) {
      if (deleted) {
        this.localDeletedItem = this.deletedItem
        this.snackbar = true
        this.getPage()
        this.reset()
      }
    }
  },

  created() {
    this.getPage()
  },

  mounted() {
    if (this.deletedItem || this.updatedItem || this.createdItem) {
      this.localDeletedItem = this.deletedItem
      this.localUpdatedItem = this.updatedItem
      this.localCreatedItem = this.createdItem

      this.snackbar = true
    }
    this.reset()
  },

  methods: {
    selectForDelete(item) {
      this.selectedForDelete = item
      this.deleteConfirmShow = true
    },

    onDeleteConfirm() {
      this.deleteConfirmShow = false
      this.deleteItem(this.selectedForDelete)
    },

    onDeleteCancel() {
      this.deleteConfirmShow = false
      this.selectedForDelete = null
    },

    reset() {
      this.updateReset()
      this.delReset()
      this.createReset()
    },

    ...mapActions({
      getPage: 'user/list/default',
      deleteItem: 'user/del/del',
      createReset: 'user/create/reset',
      delReset: 'user/del/reset',
      updateReset: 'user/update/reset'
    })
  }
}
</script>
